import { useState } from 'react';
import { X, Percent, DollarSign, Tag, Check } from 'lucide-react';
import clsx from 'clsx';

interface Discount {
  type: 'percent' | 'fixed' | 'promo';
  value: number; // percent (0-100) or cents
  code?: string;
  reason?: string;
}

interface DiscountModalProps {
  subtotal: number;
  onApply: (discount: Discount) => void;
  onCancel: () => void;
}

const presetDiscounts = [
  { label: '10%', type: 'percent' as const, value: 10 },
  { label: '15%', type: 'percent' as const, value: 15 },
  { label: '20%', type: 'percent' as const, value: 20 },
  { label: '25%', type: 'percent' as const, value: 25 },
];

const discountReasons = [
  'Manager Comp',
  'Birthday',
  'Service Issue',
  'Employee Discount',
  'Loyalty Reward',
  'Other',
];

export default function DiscountModal({ subtotal, onApply, onCancel }: DiscountModalProps) {
  const [discountType, setDiscountType] = useState<'percent' | 'fixed' | 'promo'>('percent');
  const [percentValue, setPercentValue] = useState<number | null>(null);
  const [customPercent, setCustomPercent] = useState('');
  const [fixedAmount, setFixedAmount] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoDiscount, setPromoDiscount] = useState<{ percent?: number; fixed?: number } | null>(null);
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const calculateDiscount = (): number => {
    if (discountType === 'percent') {
      const pct = customPercent ? parseFloat(customPercent) : percentValue || 0;
      return Math.round(subtotal * (pct / 100));
    }
    if (discountType === 'fixed') {
      return Math.round(parseFloat(fixedAmount || '0') * 100);
    }
    if (discountType === 'promo' && promoDiscount) {
      if (promoDiscount.percent) {
        return Math.round(subtotal * (promoDiscount.percent / 100));
      }
      return promoDiscount.fixed || 0;
    }
    return 0;
  };

  const discountAmount = calculateDiscount();
  const newTotal = subtotal - discountAmount;

  const handleApplyPromo = () => {
    // Simulate promo code validation
    // In production, this would call the API
    const validCodes: Record<string, { percent?: number; fixed?: number }> = {
      'SAVE10': { percent: 10 },
      'SAVE20': { percent: 20 },
      'FLAT5': { fixed: 500 },
      'WELCOME': { percent: 15 },
    };

    const code = promoCode.toUpperCase();
    if (validCodes[code]) {
      setPromoDiscount(validCodes[code]);
      setPromoError('');
    } else {
      setPromoError('Invalid promo code');
      setPromoDiscount(null);
    }
  };

  const handleConfirm = () => {
    const finalReason = reason === 'Other' ? customReason : reason;

    if (discountType === 'percent') {
      const pct = customPercent ? parseFloat(customPercent) : percentValue || 0;
      onApply({ type: 'percent', value: pct, reason: finalReason });
    } else if (discountType === 'fixed') {
      onApply({ type: 'fixed', value: Math.round(parseFloat(fixedAmount) * 100), reason: finalReason });
    } else if (discountType === 'promo' && promoDiscount) {
      onApply({
        type: 'promo',
        value: promoDiscount.percent || promoDiscount.fixed || 0,
        code: promoCode.toUpperCase(),
        reason: `Promo: ${promoCode.toUpperCase()}`,
      });
    }
  };

  const isValid = discountAmount > 0 && discountAmount <= subtotal;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] max-h-[90dvh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">Apply Discount</h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Discount Type */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-3">Discount Type</h3>
            <div className="flex gap-2">
              {[
                { id: 'percent', label: 'Percentage', icon: Percent },
                { id: 'fixed', label: 'Fixed Amount', icon: DollarSign },
                { id: 'promo', label: 'Promo Code', icon: Tag },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setDiscountType(id as typeof discountType)}
                  className={clsx(
                    'flex-1 p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-colors',
                    discountType === id
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  )}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Percentage Options */}
          {discountType === 'percent' && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Select Percentage</h3>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {presetDiscounts.map(({ label, value }) => (
                  <button
                    key={value}
                    onClick={() => {
                      setPercentValue(value);
                      setCustomPercent('');
                    }}
                    className={clsx(
                      'py-3 rounded-lg font-medium transition-colors',
                      percentValue === value && !customPercent
                        ? 'bg-primary-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={customPercent}
                  onChange={(e) => {
                    setCustomPercent(e.target.value);
                    setPercentValue(null);
                  }}
                  className="input w-full pr-8"
                  placeholder="Custom percentage"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
                  %
                </span>
              </div>
            </div>
          )}

          {/* Fixed Amount */}
          {discountType === 'fixed' && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Enter Amount</h3>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={fixedAmount}
                  onChange={(e) => setFixedAmount(e.target.value)}
                  className="input w-full pl-7 text-xl"
                  placeholder="0.00"
                />
              </div>
            </div>
          )}

          {/* Promo Code */}
          {discountType === 'promo' && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Enter Promo Code</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    setPromoError('');
                    setPromoDiscount(null);
                  }}
                  className="input flex-1 uppercase"
                  placeholder="Enter code"
                />
                <button
                  onClick={handleApplyPromo}
                  disabled={!promoCode}
                  className="btn btn-primary"
                >
                  Apply
                </button>
              </div>
              {promoError && (
                <p className="text-danger-500 text-sm mt-2">{promoError}</p>
              )}
              {promoDiscount && (
                <p className="text-success-600 text-sm mt-2">
                  Code applied: {promoDiscount.percent ? `${promoDiscount.percent}% off` : formatCurrency(promoDiscount.fixed || 0) + ' off'}
                </p>
              )}
            </div>
          )}

          {/* Reason (for percent and fixed) */}
          {discountType !== 'promo' && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Reason</h3>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {discountReasons.map((r) => (
                  <button
                    key={r}
                    onClick={() => setReason(r)}
                    className={clsx(
                      'py-2 px-3 rounded-lg text-sm font-medium transition-colors',
                      reason === r
                        ? 'bg-primary-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    )}
                  >
                    {r}
                  </button>
                ))}
              </div>
              {reason === 'Other' && (
                <input
                  type="text"
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  className="input w-full"
                  placeholder="Enter reason"
                />
              )}
            </div>
          )}

          {/* Preview */}
          <div className="bg-slate-100 rounded-lg p-4">
            <div className="flex justify-between mb-2">
              <span className="text-slate-600">Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between mb-2 text-success-600">
              <span>Discount</span>
              <span>-{formatCurrency(discountAmount)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between font-semibold text-lg">
              <span>New Total</span>
              <span>{formatCurrency(Math.max(0, newTotal))}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 flex gap-3">
          <button onClick={onCancel} className="btn btn-secondary flex-1">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            className="btn btn-primary flex-1 gap-2"
          >
            <Check className="w-5 h-5" />
            Apply Discount
          </button>
        </div>
      </div>
    </div>
  );
}
